import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EnquiriesInbox } from "@/pages/portal/EnquiriesInbox";

export default function Admin() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/admin/pending")
      .then((r) => setPending(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
    else setLoading(false);
  }, [user, load]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/users/${id}/decision`, { decision });
      toast.success(`Specialist ${decision === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="bg-white border border-navy/10 p-10" data-testid="admin-forbidden">
        <p className="text-sm text-navy/60">This area is restricted to CVI administrators.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-page">
      <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-2">Administration</p>
      <h1 className="font-serif text-3xl font-semibold text-navy mb-10">Access Requests</h1>

      {loading ? (
        <p className="font-mono text-xs text-navy/40 tracking-widest" data-testid="admin-loading">LOADING…</p>
      ) : pending.length === 0 ? (
        <div className="bg-white border border-navy/10 p-10 text-center" data-testid="admin-empty">
          <p className="text-sm text-navy/50">No pending access requests. The queue is clear.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-pending-list">
          {pending.map((u) => (
            <div key={u.id} className="bg-white border border-navy/10 p-6 flex flex-wrap items-center justify-between gap-6" data-testid={`pending-user-${u.id}`}>
              <div>
                <p className="font-serif text-lg font-semibold text-navy">{u.name}</p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold mt-1">{u.specialty}</p>
                <p className="text-xs text-navy/50 mt-2">
                  {u.organization} · {u.gmc_number} · {u.email}
                </p>
                <p className="font-mono text-[10px] text-navy/40 mt-1">
                  Requested {new Date(u.created_at).toLocaleDateString("en-GB")}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => decide(u.id, "approve")}
                  data-testid={`approve-button-${u.id}`}
                  className="bg-navy text-ivory font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 hover:bg-emerald-700 transition-colors"
                >
                  Approve
                </button>
                <button
                  onClick={() => decide(u.id, "reject")}
                  data-testid={`reject-button-${u.id}`}
                  className="border border-crimson/40 text-crimson font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 hover:bg-crimson hover:text-white transition-colors"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EnquiriesInbox />
    </div>
  );
}
